import { forwardRef, useState, type CSSProperties, type HTMLAttributes, type ReactNode } from "react";
import { cx } from "./fieldContext";
import { LoadingOverlay } from "../components/LoadingOverlay";

type CardSlot = "header" | "title" | "extra" | "body" | "actions" | "cover";

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  title?: ReactNode;
  extra?: ReactNode;
  size?: "small" | "default" | "medium";
  /** Innere Karte (abgesetzt in einer Karte). */
  type?: "inner";
  variant?: "outlined" | "borderless";
  bordered?: boolean;
  hoverable?: boolean;
  /** Erhält die Struktur; Erstinhalt bleibt verborgen, bestätigte Hintergrunddaten werden gedimmt. */
  loading?: boolean;
  actions?: ReactNode[];
  cover?: ReactNode;
  styles?: Partial<Record<CardSlot, CSSProperties>>;
  classNames?: Partial<Record<CardSlot, string>>;
  /** @deprecated wie bei Antd: `styles.body`. */
  bodyStyle?: CSSProperties;
  /** @deprecated wie bei Antd: `styles.header`. */
  headStyle?: CSSProperties;
}

/**
 * Fläche des Kits (Ersatz für Antd `Card`, gleiche Props). Kopf mit Titel im Oberflächenstil und
 * Ornament, rechts `extra`; Inhalt darunter.
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { title, extra, size, type, variant, bordered, hoverable, loading, actions, cover, styles, classNames, bodyStyle, headStyle, className, children, ...rest },
  ref,
) {
  const borderless = variant === "borderless" || bordered === false;
  const [revealed, setRevealed] = useState(loading !== true);
  const [animateReveal] = useState(loading === true);
  if (!revealed && loading !== true) setRevealed(true);
  const body = <div className={cx("ck-card-body", classNames?.body)} style={{ ...bodyStyle, ...styles?.body }}>{children}</div>;
  return (
    <div
      ref={ref}
      className={cx("ck-card", size === "small" && "ck-card--sm", type === "inner" && "ck-card--inner", borderless && "ck-card--borderless", hoverable && "ck-card--hoverable", loading && "is-loading", className)}
      data-card-pending={!revealed || undefined}
      data-card-async={animateReveal || undefined}
      data-card-revealed={revealed && animateReveal || undefined}
      aria-busy={loading || undefined}
      {...rest}
    >
      {cover != null ? <div className={cx("ck-card-cover", classNames?.cover)} style={styles?.cover}>{cover}</div> : null}
      {title != null || extra != null ? (
        <div className={cx("ck-card-head", classNames?.header)} style={{ ...headStyle, ...styles?.header }}>
          {title != null ? <div className={cx("ck-card-title", classNames?.title)} style={styles?.title}>{title}</div> : <span />}
          {extra != null ? <div className={cx("ck-card-extra", classNames?.extra)} style={styles?.extra}>{extra}</div> : null}
        </div>
      ) : null}
      {loading === undefined ? body : <LoadingOverlay visible={loading} scope="block">{body}</LoadingOverlay>}
      {actions?.length ? (
        <ul className={cx("ck-card-actions", classNames?.actions)} style={styles?.actions}>
          {actions.map((action, index) => <li key={index}>{action}</li>)}
        </ul>
      ) : null}
    </div>
  );
});
